import { useCallback, useEffect, useState } from "react";
import {
  Box, Stack, Typography, Paper, Chip, Alert, CircularProgress, IconButton, Tooltip, Button,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import ConfirmDialog from "../components/collectors/ConfirmDialog";
import { getBindStatus, setBindStatus } from "../services/bind";

// Texto que muestra Portal Vendor 2 (lib/app_bar/app_bar/app_bar_widget.dart) cuando el aviso está activo.
const AVISO_TITULO = "Detectamos demoras en la red de transferencias interbancarias.";
const AVISO_TEXTO =
  "Las operaciones podrían demorar en ejecutarse o confirmarse.  Cuando esté confirmada la recepción de " +
  "fondos verán el cobro en nuestra plataforma.\nSi un cliente informa que realizó una transferencia y no " +
  "puede visualizarla, podemos ayudarlo gestionando un reclamo ante el Banco o Billetera de origen de los " +
  "fondos.  Digite el ID de operación alfanumérico que visualiza en el comprobante de transferencia del " +
  "cliente, tiene 22 dígitos y encontraremos el pago por usted.";

/**
 * CP-523: "Bind Status" de WA1 (página BindStatus). Prende o apaga el aviso de demoras
 * de Bind que ven los clientes en Portal Vendor 2. Mismo endpoint de CoreDB que WA1.
 */
export default function BindStatus() {
  const [status,  setStatus]  = useState<0 | 1 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);
  const [ok,      setOk]      = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setStatus(await getBindStatus()); }
    catch (e) { setError(String(e instanceof Error ? e.message : e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const activo = status === 1;

  async function onConfirm() {
    const nuevo: 0 | 1 = activo ? 0 : 1;
    await setBindStatus(nuevo);
    // El GET está cacheado ~30 s en CoreDB: se muestra el valor que se acaba de guardar.
    setStatus(nuevo); setConfirm(false);
    setOk(nuevo === 1 ? "Aviso activado. Los clientes lo ven en el portal en 1-2 minutos."
                      : "Aviso desactivado. Desaparece del portal en 1-2 minutos.");
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Bind Status</Typography>
        <Tooltip title="Refrescar">
          <span><IconButton onClick={load} disabled={loading} size="small"><RefreshIcon /></IconButton></span>
        </Tooltip>
      </Stack>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2, maxWidth: 820 }}>
        Aviso de demoras de Bind para los clientes. Mientras está activo, Portal Vendor 2 muestra el
        mensaje de abajo a los collectors <b>AR#BIND</b> (Bind PSP y cvucollect). Los clientes de CMF y STP
        no lo ven (CP-299). Solo un admin de prd puede cambiarlo.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {ok && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setOk(null)}>{ok}</Alert>}

      <Paper sx={{ p: 2.5, borderRadius: 3, maxWidth: 820 }}>
        {status === null ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            {loading ? <CircularProgress /> : <Typography color="text.secondary">Sin datos.</Typography>}
          </Box>
        ) : (
          <>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
              <Typography sx={{ fontWeight: 700 }}>Estado:</Typography>
              <Chip size="small" label={activo ? "Aviso ACTIVO (1)" : "OK, sin aviso (0)"}
                color={activo ? "warning" : "success"} sx={{ fontWeight: 700 }} />
              <Box sx={{ flex: 1 }} />
              <Button variant="contained" color={activo ? "primary" : "warning"}
                onClick={() => setConfirm(true)} disabled={loading}>
                {activo ? "Desactivar aviso" : "Activar aviso"}
              </Button>
            </Stack>

            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
              Vista previa del aviso en el portal:
            </Typography>
            <Box sx={{ bgcolor: "#FBDDB9", border: "2px solid", borderColor: "warning.main", p: 1.5,
              opacity: activo ? 1 : 0.6 }}>
              <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{AVISO_TITULO}</Typography>
              <Typography sx={{ fontSize: 13, whiteSpace: "pre-line" }}>{AVISO_TEXTO}</Typography>
            </Box>
          </>
        )}
      </Paper>

      <ConfirmDialog open={confirm} onClose={() => setConfirm(false)} onConfirm={onConfirm}
        title={activo ? "Desactivar aviso de Bind" : "Activar aviso de Bind"}
        okLabel={activo ? "Desactivar" : "Activar"} busyLabel="Guardando…" danger={!activo}>
        {activo
          ? <>Se deja de mostrar el aviso de demoras a los clientes <b>AR#BIND</b> en el portal.</>
          : <>Todos los clientes <b>AR#BIND</b> van a ver en el portal el aviso de demoras en las transferencias, hasta que lo desactives.</>}
      </ConfirmDialog>
    </Box>
  );
}
